import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ProfileEditForm from "@/pages/profile/ProfileEditForm";
import { PandaCover } from "@/pages/profile/ProfileHero";
import { compressImage } from "@/components/WallComposer";

const EMPTY = {
  display_name: "", bio: "", avatar_url: "", cover_url: "", desires: [], tags: [], show_age: true, age: "", height_cm: "", weight_kg: "",
  body_type: "", show_position: true, position: "", ethnicity: "", relationship_status: "", show_tribes: true, my_tribes: [], tribes_into: [],
  looking_for: [], meet_at: [], accepts_nsfw: false, gender: "", pronouns: "", hiv_status: "", last_tested_date: "",
  instagram_handle: "", x_handle: "", facebook_handle: "", spotify_handle: "", tiktok_handle: "", snapchat_handle: "", handle_public: true, side: null,
};
const IMG_OK = /^image\/(jpeg|png|webp|gif|heic|heif)$/;

/** Edit Profile — separate page (/u/:handle/edit). Photo, header image, bio, username visibility, links, privacy, notifications. */
export default function EditProfilePage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user: me, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [desiresCatalog, setDesiresCatalog] = useState([]);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/users/${handle}`);
    const { data: me } = await api.get("/auth/me");
    setProfile({ ...data, handle: me.handle, email: me.email, preferred_name_auto: me.preferred_name_auto });
    const next = { ...EMPTY };
    for (const k of Object.keys(EMPTY)) if (data[k] !== undefined && data[k] !== null) next[k] = data[k];
    next.handle_public = data.handle_hidden !== true;
    for (const k of ["age", "height_cm", "weight_kg"]) next[k] = data[k] ?? "";
    setForm(next);
  }, [handle]);

  useEffect(() => { window.scrollTo(0, 0); load().catch(() => setProfile(false)); }, [load]);
  useEffect(() => { api.get("/users/desires/catalog").then(({ data }) => setDesiresCatalog(Array.isArray(data?.tags) ? data.tags : [])).catch(() => {}); }, []);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", await compressImage(file));
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url + (data.url.includes("?") ? "&" : "?") + "v=" + Date.now();
  };
  const validImage = (file) => {
    const t = (file.type || "").toLowerCase();
    if (!(IMG_OK.test(t) || !t || t === "application/octet-stream" || t === "image/*")) { toast.error("Use a JPG, PNG, WEBP, GIF, or HEIC photo."); return false; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Image too large (max 25MB)."); return false; }
    return true;
  };
  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0]; if (avatarInputRef.current) avatarInputRef.current.value = "";
    if (!file || !validImage(file)) return;
    setAvatarUploading(true);
    try {
      const url = await upload(file);
      const { data } = await api.put("/users/me", { avatar_url: url });
      setForm((f) => ({ ...f, avatar_url: url })); setUser({ ...data, avatar_url: url }); toast.success("Profile picture updated.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); } finally { setAvatarUploading(false); }
  };
  const onPickCover = async (e) => {
    const file = e.target.files?.[0]; if (coverInputRef.current) coverInputRef.current.value = "";
    if (!file || !validImage(file)) return;
    setCoverUploading(true);
    try {
      const url = await upload(file);
      const { data } = await api.put("/users/me", { cover_url: url });
      setForm((f) => ({ ...f, cover_url: url })); setUser(data); toast.success("Header image updated.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); } finally { setCoverUploading(false); }
  };

  const save = async () => {
    try {
      const payload = { ...form };
      for (const k of ["age", "height_cm", "weight_kg"]) payload[k] = payload[k] === "" || payload[k] == null ? null : Number.parseInt(payload[k], 10);
      const { data } = await api.put("/users/me", payload);
      setUser(data);
      (data?.warnings || []).slice(0, 3).forEach((w) => toast.warning(w));
      toast.success("Profile updated.");
      navigate(`/u/${data.handle || handle}`);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(Array.isArray(raw) ? raw.map((x) => x?.msg || JSON.stringify(x)).join(" · ") : (typeof raw === "string" ? raw : "Could not save."));
    }
  };

  if (profile === false) return <div className="p-6"><div className="card-surface p-12 text-center font-display text-2xl">User not found.</div></div>;
  if (!profile || !me) return null;
  if (me.id !== profile.id) return <div className="p-6"><div className="card-surface p-12 text-center text-sm text-zinc-400">Only {profile.display_name} can edit this profile.</div></div>;

  return (
    <div data-testid="edit-profile-page" className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <Link to={`/u/${handle}`} data-testid="edit-profile-back" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-white"><ArrowLeft size={12} /> Back to profile</Link>
        <h1 className="font-display text-2xl">Edit Profile</h1>
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-6" data-testid="edit-profile-cover">
        {form.cover_url ? <img src={form.cover_url} alt="" className="h-40 w-full object-cover" /> : <PandaCover className="h-40 w-full" />}
        <button type="button" data-testid="edit-profile-cover-button" onClick={() => coverInputRef.current?.click()} disabled={coverUploading} className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-black/60 backdrop-blur border border-white/20 text-xs text-white hover:border-emerald-400">
          {coverUploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />} Header image
        </button>
        <input ref={coverInputRef} type="file" accept="image/*" onChange={onPickCover} className="hidden" data-testid="edit-profile-cover-input" />
      </div>

      <div className="card-surface p-5 sm:p-6">
        <ProfileEditForm
          form={form}
          onFormChange={setForm}
          desiresCatalog={desiresCatalog}
          avatarInputRef={avatarInputRef}
          avatarUploading={avatarUploading}
          profile={profile}
          onSave={save}
          onCancel={() => navigate(`/u/${handle}`)}
          onTwoFactorToggled={(next) => setProfile((p) => (p ? { ...p, two_factor_enabled: next } : p))}
        />
        <input ref={avatarInputRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="edit-profile-avatar-input" />
      </div>
      <div className="mt-4 flex justify-end">
        <Button variant="ghost" onClick={() => navigate(`/u/${handle}`)} className="text-zinc-400">Done</Button>
      </div>
    </div>
  );
}
